import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { startMicrosoftLogin } from '@/core/api';
import { useAuth } from '@/core/auth';
import { useIntl } from '@/core/i18n/use-intl';
import { Button, Separator } from '@/shared/components/ui';

import { LoginForm } from '../components/login-form';
import { MicrosoftIcon } from '../components/microsoft-icon';

export function Login() {
  const { t } = useIntl();
  const { login, loading: isLoading } = useAuth();
  const location = useLocation();
  const [isMicrosoftLoading, setIsMicrosoftLoading] = useState(false);

  // Get email from URL query parameter
  const queryParams = new URLSearchParams(location.search);
  const emailFromQuery = queryParams.get('email') || '';

  const defaultEmail =
    emailFromQuery || (import.meta.env.DEV ? import.meta.env.VITE_DEV_LOGIN_EMAIL || '' : '');
  const defaultPassword = import.meta.env.DEV ? import.meta.env.VITE_DEV_LOGIN_PASSWORD || '' : '';

  async function handleLogin(values: { email: string; password: string }) {
    await login({
      email: values.email,
      password: values.password,
    });
  }

  async function handleMicrosoftLogin() {
    // Get redirect path from query parameters or default to /
    const redirectPath = queryParams.get('redirect') || '/';
    // Mode can be specified via URL parameter for GMN vs customer login
    // e.g., /auth/login?mode=gmn for GMN admin login
    const mode = (queryParams.get('mode') as 'gmn' | 'customer') || undefined;

    setIsMicrosoftLoading(true);
    try {
      await startMicrosoftLogin({
        redirectPath,
        mode,
      });
    } catch (_error) {
      // Error is already handled by the API layer
      setIsMicrosoftLoading(false);
    }
  }

  const isAnyLoading = isLoading || isMicrosoftLoading;

  return (
    <div>
      <div className="grid gap-2 text-center">
        <h1 className="text-3xl font-bold">{t('auth.login')}</h1>
        <p className="text-muted-foreground text-balance">{t('auth.loginDescription')}</p>
      </div>

      <div className="mt-6">
        <Button
          variant="outline"
          className="w-full"
          onClick={handleMicrosoftLogin}
          disabled={isAnyLoading}
        >
          <MicrosoftIcon className="mr-2 h-4 w-4" />
          {t('auth.loginWithMicrosoft')}
        </Button>
      </div>

      <div className="relative my-6">
        <div className="absolute inset-0 flex items-center">
          <Separator className="w-full" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-background px-2 text-muted-foreground">
            {t('auth.orContinueWith')}
          </span>
        </div>
      </div>

      <LoginForm
        onSubmit={handleLogin}
        isLoading={isLoading}
        defaultEmail={defaultEmail}
        defaultPassword={defaultPassword}
      />
    </div>
  );
}
